import React from 'react';
import CollapsibleTable from '../CollapsibleTable';

interface RecommendationItem {
  title: string;
  current_issue: string;
  recommended_solution: string;
  implementation: string;
  expected_impact: string;
}

interface RecommendationSection {
  priority_level: string;
  priority_description: string;
  items: RecommendationItem[];
}

interface ExpertRecommendationsJSON {
  critical_changes?: RecommendationSection;
  high_impact_improvements?: RecommendationSection;
  optimization_opportunities?: RecommendationSection;
}

interface ExpertRecommendationsProps {
  recommendations: string | ExpertRecommendationsJSON;
  adTitle: string;
  isExporting?: boolean;
  className?: string;
}

const ExpertRecommendations: React.FC<ExpertRecommendationsProps> = ({
  recommendations,
  isExporting = false,
  className = '',
}) => {
  const [showFullContent, setShowFullContent] = React.useState(false);

  // Check if recommendations is JSON object or string
  const isJsonFormat = typeof recommendations === 'object';
  const recommendationsData = isJsonFormat ? (recommendations as ExpertRecommendationsJSON) : null;
  const recommendationsString = typeof recommendations === 'string' ? recommendations : '';

  // Helper function to truncate text to first 5-6 lines
  const getPreviewText = (
    text: string,
    maxLines: number = 5,
  ): { preview: string; hasMore: boolean } => {
    const lines = text.split('\n');
    if (lines.length <= maxLines) {
      return { preview: text, hasMore: false };
    }
    return {
      preview: lines.slice(0, maxLines).join('\n') + '...',
      hasMore: true,
    };
  };

  // Render JSON format using elegant table structure like surveys
  const renderJsonRecommendations = (data: ExpertRecommendationsJSON) => {
    const sections = [
      { key: 'critical_changes', data: data.critical_changes },
      { key: 'high_impact_improvements', data: data.high_impact_improvements },
      { key: 'optimization_opportunities', data: data.optimization_opportunities },
    ];

    // Get only the first section for preview
    const firstSection = sections.find(
      ({ data: section }) => section && section.items && section.items.length > 0,
    );
    if (!firstSection) return null;

    const section = firstSection.data!;
    const previewItems = showFullContent ? section.items : section.items.slice(0, 1);
    const hasMoreItems = section.items.length > 1;

    // Convert section data to table format like surveys
    const tableData = {
      type: 'table' as const,
      title: `${section.priority_level} (${showFullContent ? section.items.length : previewItems.length} of ${section.items.length} recommendations)`,
      headers: ['Recommendation', 'Current Issue', 'Solution', 'Implementation', 'Expected Impact'],
      data: previewItems.map((item) => ({
        Recommendation: item.title,
        'Current Issue': item.current_issue,
        Solution: item.recommended_solution,
        Implementation: item.implementation,
        'Expected Impact': item.expected_impact,
      })),
    };

    return (
      <div className="space-y-4">
        {/* Section Description */}
        {section.priority_description && (
          <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-300">
            <p className="text-sm text-gray-700 leading-relaxed">{section.priority_description}</p>
          </div>
        )}

        {/* Elegant Table like surveys */}
        <CollapsibleTable
          table={tableData}
          questionType="expert_recommendations"
          isExporting={isExporting}
          maxInitialRows={showFullContent ? 10 : 1}
          showFullContent={true}
          showExportButtons={!isExporting}
        />

        {/* See More / See Less button */}
        {hasMoreItems && !isExporting && (
          <div className="text-center pt-2">
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="text-sm text-[#07E5D1] hover:text-[#06D4C2] font-medium transition-colors"
            >
              {showFullContent
                ? 'See Less'
                : `See More (${section.items.length - 1} more recommendations)`}
            </button>
          </div>
        )}

        {/* Show other sections when expanded */}
        {showFullContent && (
          <div className="space-y-6 pt-4">
            {sections.slice(1).map(({ key, data: otherSection }) => {
              if (!otherSection || !otherSection.items || otherSection.items.length === 0)
                return null;

              const otherTableData = {
                type: 'table' as const,
                title: `${otherSection.priority_level} (${otherSection.items.length} recommendations)`,
                headers: [
                  'Recommendation',
                  'Current Issue',
                  'Solution',
                  'Implementation',
                  'Expected Impact',
                ],
                data: otherSection.items.map((item) => ({
                  Recommendation: item.title,
                  'Current Issue': item.current_issue,
                  Solution: item.recommended_solution,
                  Implementation: item.implementation,
                  'Expected Impact': item.expected_impact,
                })),
              };

              return (
                <div key={key} className="space-y-4">
                  {/* Section Description */}
                  {otherSection.priority_description && (
                    <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-300">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {otherSection.priority_description}
                      </p>
                    </div>
                  )}

                  {/* Elegant Table like surveys */}
                  <CollapsibleTable
                    table={otherTableData}
                    questionType="expert_recommendations"
                    isExporting={isExporting}
                    maxInitialRows={10}
                    showFullContent={true}
                    showExportButtons={!isExporting}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render string format (legacy) - elegant table approach with preview
  const renderStringRecommendations = (text: string) => {
    const { preview, hasMore } = getPreviewText(text, 5);
    const displayText = showFullContent ? text : preview;

    // Create a single table with the markdown content
    const tableData = {
      type: 'table' as const,
      title: 'Expert Recommendations',
      headers: ['Recommendations'],
      data: [
        {
          Recommendations: displayText,
        },
      ],
    };

    return (
      <div className="space-y-4">
        <CollapsibleTable
          table={tableData}
          questionType="expert_recommendations_text"
          isExporting={isExporting}
          maxInitialRows={1}
          showFullContent={true}
          showExportButtons={!isExporting}
        />

        {/* See More / See Less button */}
        {hasMore && !isExporting && (
          <div className="text-center pt-2">
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              className="text-sm text-[#07E5D1] hover:text-[#06D4C2] font-medium transition-colors"
            >
              {showFullContent ? 'See Less' : 'See More'}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Recommendations Content */}
      {isJsonFormat
        ? renderJsonRecommendations(recommendationsData!)
        : renderStringRecommendations(recommendationsString)}
    </div>
  );
};

export default ExpertRecommendations;
